/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { updateDashboardMessage } from "../../../../../state/thunks/settings";
import {resetToDB, updateCachedNotificationsConfig, updateUpdateStatus} from "../../../../../state/slices/settings";

// material
import { Switch, TextField } from "@insticator/insticator-ui";
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import SectionCard from "../../../../../material/expandablesection/sectioncard/sectioncard";
import SectionGroupDescription
  from "../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription";
import SectionGroup from "../../../../../material/expandablesection/sectiongroup/sectiongroup";

// styles
import injectSheet from 'react-jss';
import styles from './notifications_styles';

// static
const SECTION_TITLE = 'Notifications'
const UPDATE_STATUS = 'notificationsUpdateStatus'
const STATUS_IDLE = 'idle'
const SUCCESS_MESSAGE = 'Update Success'
const ERROR_MESSAGE = 'Update Failed'
const DATA_DELAY_DESCRIPTION = 'If enabled, ALL customer Publisher Portal users will see the below message: ' +
  'There is an unexpected delay releasing the Monetization data. We will roll out updates as quickly as possible.'

/* ========== ~~~~~~~~~~ Usage Fees ~~~~~~~~~~ ========== */
const Notifications = injectSheet(styles)((props) => {
  const dispatch = useDispatch()
  const {
    notificationsConfig: {
      showMessage,
      message
    },
    notificationsConfigFromDB,
    notificationsUpdateStatus
  } = useSelector((state) => state.settings)
  const [isOpened, setIsOpened] = useState(false)
  const [isChanged, setIsChanged] = useState(false)

  /*************** ASYNC ***************/
  useEffect(() => {
    if (showMessage === notificationsConfigFromDB?.showMessage &&
      message === notificationsConfigFromDB?.message) {
      setIsChanged(false)
    } else {
      setIsChanged(true)
    }
  }, [showMessage, message])

  useEffect(() => {
    if (notificationsUpdateStatus === 'success' || notificationsUpdateStatus === 'failed') setTimeout(() => {
      dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    }, [2000])
  }, [notificationsUpdateStatus])

  /*************** EVENT HANDLERS ***************/
  const handleToggle = () => {
    if (notificationsUpdateStatus !== STATUS_IDLE) dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    setIsOpened(!isOpened)
  }

  const handleCancel = () => {
    dispatch(resetToDB({ 'cache': 'notificationsConfig', 'db': 'notificationsConfigFromDB'}))
    setIsOpened(false)
  }

  const handleSave = () => {
    dispatch(updateDashboardMessage({ showMessage: showMessage, message: message }))
    setIsOpened(false)
  }

  const handleToggleUpdate = (target, val) => {
    dispatch(updateCachedNotificationsConfig({ showMessage: val, message: message }))
  }

  const handleChange = e => {
    dispatch(updateCachedNotificationsConfig({ showMessage: showMessage, message: e.target.value }))
  }

  /*************** UI ***************/
  let statusMessage = "";
  switch (notificationsUpdateStatus) {
    case "success":
      statusMessage = SUCCESS_MESSAGE
      break;
    case "failed":
      statusMessage = ERROR_MESSAGE
      break;
    case "idle":
      statusMessage = ""
      break;
  }

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged}
      title={SECTION_TITLE}
      description={statusMessage}
      descriptionClass={notificationsUpdateStatus === "success" ? props.classes.updateSuccess : props.classes.updateFailed}
      />
  )

  return (
    <ExpandableSection
      controller={controller}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged}
      >
      {isOpened && <>
        <SectionCard klass={props.classes.notifications}>
          <SectionGroup>
            <Switch
              variant={'primary'}
              isStatic={false}
              on={showMessage}
              handleClick={handleToggleUpdate}
            />
            <SectionGroupDescription
              title={'Publisher Portal 2.0 data delay message'}
              description={DATA_DELAY_DESCRIPTION}
            />
          </SectionGroup>
          <SectionGroup>
            <SectionGroupDescription
              title={'Publisher Portal Message'}
              description=<TextField value={message} klass={'text'} placeholder={'Type to create Publisher Portal message'}
                            handleChange={handleChange}/>
            />
          </SectionGroup>
        </SectionCard>
      </>}
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Notifications;